import { useQueryStates } from 'nuqs';
import { useMemo } from 'react';
import type { ContactResolveRef } from '@/hooks/queries/use-resolve-contact-query';
import { contactDrawerParsers } from './contact-drawer-url-state';
import { platformEnumOf } from './platform-of';

/**
 * Builds the `{ entryType, id }` ref the resolve query needs from the drawer's
 * URL state (`contactId` + `contactEntry`).
 *
 * For a creator entry the ref also carries the `platform` derived from the
 * clicked `contactPlatform`, so the backend resolves the handle against the right
 * platform (the same handle on IG and TikTok are two different people). The
 * affiliate path omits it — the backend resolves an affiliate by user id alone.
 *
 * Returns `null` when the entry is missing or unrecognised — keeping the resolve
 * query disabled. The drawer proceeds with an empty-header shell while loading,
 * and fills in once the query settles.
 */
export const useContactResolveRef = (): ContactResolveRef | null => {
  const [{ contactId, contactEntry, contactSourceCreatorId, contactPlatform }] =
    useQueryStates(contactDrawerParsers);

  return useMemo(() => {
    if (!contactId) return null;
    if (contactEntry === 'creator') {
      // A creator opened from search / recommendations carries its dataset id;
      // forward it so the backend can provision an identity for a creator that
      // is not yet wishlisted (otherwise the card fails to resolve).
      return {
        entryType: contactEntry,
        id: contactId,
        platform: platformEnumOf(contactPlatform),
        sourceCreatorId: contactSourceCreatorId ?? undefined,
      };
    }
    if (contactEntry === 'source') {
      // A prospect the plan masks: the backend resolves it from its dataset
      // reference, on the platform the brand opened.
      return { entryType: contactEntry, id: contactId, platform: platformEnumOf(contactPlatform) };
    }
    if (contactEntry === 'affiliate' || contactEntry === 'identity') {
      // Identity entries (collab tracking rows) already carry the identity id;
      // the backend still resolves it to gate access and fill the header.
      return { entryType: contactEntry, id: contactId };
    }
    return null;
  }, [contactId, contactEntry, contactSourceCreatorId, contactPlatform]);
};
